import { z } from 'zod';
import { IsoDate, IsoDateTime, Uuid } from './common.js';
import { MonthKey } from './metrics.js';

/**
 * Lot BM [17] [18] (CDC « Chiffres, simulations, Agenda et Apporteurs », contrat GELÉ du 23/10/2026) : vue « R1 et R2
 * par mois » de l'Agenda et reprise de l'historique des agendas.
 */

export const AgendaMonthlyQuery = z.object({ year: z.coerce.number().int().min(2000).max(2100).optional() });
export type AgendaMonthlyQuery = z.infer<typeof AgendaMonthlyQuery>;

/** Un rendez-vous R1 ou R2 du mois (copies d'un même événement réunies, comme la semaine de l'agenda). */
export const AgendaMonthlyMeeting = z.object({
  /** Clé du groupe de copies (`eventGroupKey`). */
  key: z.string(),
  kind: z.enum(['R1', 'R2']),
  startAt: IsoDateTime,
  /** Titre de l'événement (copie principale). */
  title: z.string(),
  /** Prospect : le nom du client relié, sinon le reste du titre (« R1 Menuiserie Coutant » → « Menuiserie Coutant »). */
  prospect: z.string(),
  clientId: Uuid.nullable(),
  /** Membres présents (`r1MemberIds` : propriétaires des copies, puis invités internes), prénoms. */
  members: z.array(z.string()),
  /** Rendez-vous après aujourd'hui (pas encore compté dans `r1` / `r2`). */
  upcoming: z.boolean(),
});
export type AgendaMonthlyMeeting = z.infer<typeof AgendaMonthlyMeeting>;

/**
 * Un mois de la vue. `r1` / `r2` : rendez-vous passés (jusqu'à aujourd'hui inclus) de toute l'équipe, copies d'un même
 * événement comptées une fois (même règle que les R1 des Chiffres, `r1ByMonth`). Un mois qui commence avant le début de
 * lecture des agendas (`coveredFrom`) : `r1` = R1 de l'historique du Google Sheet (`r1FromSheet`, null sans valeur) et
 * `r2` = null (« — », inconnus). `r1Upcoming` / `r2Upcoming` : déjà posés, à venir.
 */
export const AgendaMonthlyMonth = z.object({
  month: MonthKey,
  r1: z.int().min(0).nullable(),
  r2: z.int().min(0).nullable(),
  r1Upcoming: z.int().min(0),
  r2Upcoming: z.int().min(0),
  r1FromSheet: z.boolean(),
  /** Rendez-vous du mois lus dans les agendas (vide pour un mois venu du Sheet), dans l'ordre. */
  meetings: z.array(AgendaMonthlyMeeting).default([]),
});
export type AgendaMonthlyMonth = z.infer<typeof AgendaMonthlyMonth>;

/** État de la reprise de l'historique des agendas (`sync_cursors`, ligne `calendar.backfill`). */
export const CalendarBackfillState = z.enum(['never', 'running', 'done', 'error']);
export type CalendarBackfillState = z.infer<typeof CalendarBackfillState>;

export const CalendarBackfillStatus = z.object({
  state: CalendarBackfillState,
  /** Jour demandé (« depuis le 1er janvier »). */
  from: IsoDate.nullable(),
  startedAt: IsoDateTime.nullable(),
  finishedAt: IsoDateTime.nullable(),
  /** Erreur (état `error`) ou agendas illisibles. */
  message: z.string().nullable(),
  /** Événements lus par la reprise (copies). */
  copies: z.int().min(0).nullable(),
  /** Agendas reliés sans reprise depuis `from` (0 : rien à reprendre, le bouton disparaît). */
  pendingCalendars: z.int().min(0),
});
export type CalendarBackfillStatus = z.infer<typeof CalendarBackfillStatus>;

/** `GET /api/agenda/monthly?year=`. */
export const AgendaMonthly = z.object({
  year: z.int(),
  today: IsoDate,
  /** Jour depuis lequel TOUS les agendas lus sont couverts (`calendar.coveredFrom`), null = aucun agenda lu. */
  coveredFrom: IsoDate.nullable(),
  months: z.array(AgendaMonthlyMonth).length(12),
  backfill: CalendarBackfillStatus,
});
export type AgendaMonthly = z.infer<typeof AgendaMonthly>;

/** `POST /api/admin/calendar/backfill { from }` (administrateurs). */
export const CalendarBackfillBody = z.object({ from: IsoDate });
export type CalendarBackfillBody = z.infer<typeof CalendarBackfillBody>;
